import { useRef, useCallback } from 'react';

export const useVoteWorker = () => {
  const workerRef = useRef(null);
  
  const initWorker = useCallback(() => {
    if (!workerRef.current) {
      try {
        // Load worker from public folder (works in both dev and production)
        workerRef.current = new Worker('/voteCalculator.js');
        console.log('✅ Web Worker initialized successfully');
      } catch (error) {
        console.warn('⚠️ Web Worker not supported, falling back to main thread:', error);
        return null;
      }
    }
    return workerRef.current;
  }, []);
  
  const calculateOverallResults = useCallback((voteResults) => {
    return new Promise((resolve, reject) => {
      const worker = initWorker();
      
      if (!worker) {
        // Fallback to main thread calculation
        try {
          const allResults = {};
          
          Object.entries(voteResults).forEach(([categoryId, categoryResults]) => {
            Object.entries(categoryResults).forEach(([movieId, movieData]) => {
              if (!allResults[movieId]) {
                allResults[movieId] = {
                  votes: [],
                  totalVotes: 0,
                  averageScore: 0,
                  distribution: { '-1': 0, '0': 0, '1': 0, '2': 0, '3': 0 }
                };
              }
              
              if (movieData.votes && Array.isArray(movieData.votes)) {
                allResults[movieId].votes.push(...movieData.votes);
              }
              allResults[movieId].totalVotes += movieData.totalVotes || 0;
              
              if (movieData.distribution) {
                Object.keys(movieData.distribution).forEach(score => {
                  allResults[movieId].distribution[score] += movieData.distribution[score] || 0;
                });
              }
            });
          });
          
          Object.keys(allResults).forEach(movieId => {
            const movieVotes = allResults[movieId];
            const totalScore = movieVotes.votes.reduce((sum, vote) => sum + (vote.voteValue || 0), 0);
            movieVotes.averageScore = movieVotes.totalVotes > 0 ? totalScore / movieVotes.totalVotes : 0;
          });
          
          resolve(allResults);
        } catch (error) {
          reject(error);
        }
        return;
      }
      
      const handleMessage = (e) => {
        const { type, data, success, error } = e.data;
        
        if (type === 'OVERALL_RESULTS_COMPLETE' && success) {
          worker.removeEventListener('message', handleMessage);
          worker.removeEventListener('error', handleError);
          resolve(data);
        } else if (type === 'ERROR' || !success) {
          worker.removeEventListener('message', handleMessage);
          worker.removeEventListener('error', handleError);
          reject(new Error(error || 'Worker calculation failed'));
        }
      };
      
      const handleError = (error) => {
        worker.removeEventListener('message', handleMessage);
        worker.removeEventListener('error', handleError);
        reject(new Error(`Worker error: ${error.message}`));
      };
      
      worker.addEventListener('message', handleMessage);
      worker.addEventListener('error', handleError);
      
      worker.postMessage({
        type: 'CALCULATE_OVERALL_RESULTS',
        data: { voteResults }
      });
      
      // Timeout fallback
      setTimeout(() => {
        worker.removeEventListener('message', handleMessage);
        reject(new Error('Worker calculation timeout'));
      }, 10000); // 10 second timeout
    });
  }, [initWorker]);
  
  const processVoteData = useCallback((votesData) => {
    return new Promise((resolve, reject) => {
      const worker = initWorker();
      
      if (!worker) {
        // Fallback to main thread
        try {
          const votesByCategory = {};
          
          for (let i = 0; i < votesData.length; i++) {
            const vote = votesData[i];
            
            if (!votesByCategory[vote.category_id]) {
              votesByCategory[vote.category_id] = {};
            }
            
            if (!votesByCategory[vote.category_id][vote.movie_id]) {
              votesByCategory[vote.category_id][vote.movie_id] = {
                votes: [],
                totalVotes: 0,
                averageScore: 0,
                distribution: { '-1': 0, '0': 0, '1': 0, '2': 0, '3': 0 }
              };
            }
            
            const movieVotes = votesByCategory[vote.category_id][vote.movie_id];
            movieVotes.votes.push({
              userEmail: vote.user_email,
              voteValue: vote.vote_value,
              timestamp: vote.updated_at
            });
            movieVotes.totalVotes++;
            movieVotes.distribution[vote.vote_value]++;
          }
          
          Object.keys(votesByCategory).forEach(categoryId => {
            Object.keys(votesByCategory[categoryId]).forEach(movieId => {
              const movieVotes = votesByCategory[categoryId][movieId];
              const totalScore = movieVotes.votes.reduce((sum, vote) => sum + vote.voteValue, 0);
              movieVotes.averageScore = movieVotes.totalVotes > 0 ? totalScore / movieVotes.totalVotes : 0;
            });
          });
          
          resolve(votesByCategory);
        } catch (error) {
          reject(error);
        }
        return;
      }
      
      const handleMessage = (e) => {
        const { type, data, success, error } = e.data;
        
        if (type === 'VOTE_DATA_PROCESSED' && success) {
          worker.removeEventListener('message', handleMessage);
          resolve(data);
        } else if (type === 'ERROR' || !success) {
          worker.removeEventListener('message', handleMessage);
          reject(new Error(error || 'Worker processing failed'));
        }
      };
      
      worker.addEventListener('message', handleMessage);
      worker.postMessage({
        type: 'PROCESS_VOTE_DATA',
        data: { votesData }
      });
      
      // Timeout fallback
      setTimeout(() => {
        worker.removeEventListener('message', handleMessage);
        reject(new Error('Worker processing timeout'));
      }, 10000);
    });
  }, [initWorker]);
  
  const cleanup = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
  }, []);
  
  return {
    calculateOverallResults,
    processVoteData,
    cleanup
  };
};
